import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { RefreshCw, Search } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { AlertReviewCard } from '@/components/alerts/AlertReviewCard'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAlertReview, useAlertReviews } from '@/hooks/useAlertReviews'
import type { ReviewedAlert } from '@/lib/alertReviews'

const PAGE_SIZE = 50

export default function AlertReviews() {
  const [searchParams, setSearchParams] = useSearchParams()
  const symbol = searchParams.get('symbol') || ''
  const selectedId = searchParams.get('alert_review_id') || ''
  const selected = useAlertReview(selectedId)
  const [symbolDraft, setSymbolDraft] = useState(symbol)
  const [pendingOnly, setPendingOnly] = useState(true)
  const [offset, setOffset] = useState(0)
  const query = useAlertReviews({ symbol, pendingOnly, offset, limit: PAGE_SIZE })
  const groups = useMemo(() => {
    const grouped = new Map<string, ReviewedAlert[]>()
    for (const alert of query.data?.alerts || []) {
      if (alert.id === selected.data?.alert.id) continue
      grouped.set(alert.symbol, [...(grouped.get(alert.symbol) || []), alert])
    }
    return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [query.data, selected.data?.alert.id])
  const total = query.data?.total || 0
  useEffect(() => {
    if (query.data && offset > 0 && offset >= query.data.total) {
      setOffset(Math.max(0, Math.ceil(query.data.total / PAGE_SIZE) - 1) * PAGE_SIZE)
    }
  }, [query.data, offset])

  return <div className="flex flex-col">
    <Header title="Alert reviews" />
    <div className="flex-1 space-y-6 p-4 sm:p-6">
      <p className="max-w-3xl text-sm text-muted-foreground">
        Review triggered alerts together by symbol, including checked alerts and all saved notes.
        {' '}In Telegram, <code>/review_alerts</code> posts this account’s current queue and clears reported alerts after every page is delivered.
        {' '}New alerts and later notes start the next period. Full history stays available.
      </p>
      <p className="text-sm text-muted-foreground">
        Last successful report: {query.data?.last_report_at
          ? new Date(query.data.last_report_at * 1000).toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC')
          : 'No report yet'}.
        {' '}Group reports include shared alerts from all destinations; personal alerts are included in private chat.
      </p>
      {selectedId && <section className="space-y-3" aria-label="Selected alert review">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Selected alert{selected.data ? ` · ${selected.data.alert.symbol}` : ''}</h2>
          <Button variant="ghost" size="sm" onClick={() => setSearchParams((previous) => {
            const next = new URLSearchParams(previous); next.delete('alert_review_id'); return next
          })}>Back to list</Button>
        </div>
        {selected.error ? <p role="alert" className="text-sm text-destructive">{selected.error instanceof Error ? selected.error.message : 'Could not load this alert.'}</p>
          : selected.data ? <AlertReviewCard key={selected.data.alert.id} alert={selected.data.alert} initiallyEditing />
          : <p role="status" className="text-sm text-muted-foreground">Loading selected alert…</p>}
      </section>}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2" aria-label="Review status">
          <Button variant={pendingOnly ? 'default' : 'outline'} size="sm" aria-pressed={pendingOnly} onClick={() => { setPendingOnly(true); setOffset(0) }}>Current period</Button>
          <Button variant={!pendingOnly ? 'default' : 'outline'} size="sm" aria-pressed={!pendingOnly} onClick={() => { setPendingOnly(false); setOffset(0) }}>All history</Button>
        </div>
        <form className="flex flex-wrap items-center gap-2" onSubmit={(event) => {
          event.preventDefault(); setSearchParams(symbolDraft.trim() ? { symbol: symbolDraft.trim().toUpperCase() } : {}); setOffset(0)
        }}>
          <Input aria-label="Filter reviews by symbol" placeholder="Symbol, e.g. BTC" value={symbolDraft} maxLength={80} onChange={(event) => setSymbolDraft(event.target.value)} className="w-44" />
          <Button type="submit" variant="outline" size="sm"><Search className="mr-2 h-4 w-4" />Filter</Button>
          {symbol && <Button type="button" variant="ghost" size="sm" onClick={() => { setSymbolDraft(''); setSearchParams({}); setOffset(0) }}>Clear</Button>}
          <Button type="button" variant="outline" size="sm" disabled={query.isFetching} onClick={() => void query.refetch()}><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
        </form>
      </div>
      {query.error ? <p role="alert" className="text-sm text-destructive">{query.error instanceof Error ? query.error.message : 'Could not load alert reviews.'}</p>
        : query.isLoading ? <p role="status" className="py-10 text-center text-muted-foreground">Loading alert reviews…</p>
        : <>
          <p className="text-xs text-muted-foreground" role="status">{total ? `Showing ${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total} alerts, grouped by symbol.` : '0 alerts'}{selected.data && ' The selected alert is shown above.'}</p>
          {groups.length === 0 ? <div className="rounded-lg border p-10 text-center text-muted-foreground">
            {selected.data ? 'No other alerts in this view.' : pendingOnly ? 'No alerts or new notes since your last successful report.' : 'No alerts recorded in this view yet.'}
          </div> : groups.map(([groupSymbol, alerts]) => <section key={groupSymbol} aria-label={`${groupSymbol} reviews`} className="space-y-3">
            <h2 className="text-lg font-semibold">{groupSymbol} <span className="text-sm font-normal text-muted-foreground">({alerts.length})</span></h2>
            {alerts.map((alert) => <AlertReviewCard key={alert.id} alert={alert} />)}
          </section>)}
          {(offset > 0 || total > PAGE_SIZE) && <div className="flex items-center justify-between gap-3">
            <Button variant="outline" size="sm" disabled={offset === 0 || query.isFetching} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</Button>
            <span className="text-sm text-muted-foreground">Page {Math.floor(offset / PAGE_SIZE) + 1}</span>
            <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= total || query.isFetching} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</Button>
          </div>}
        </>}
    </div>
  </div>
}
